/**
 * Collctiv design tokens.
 *
 * Sources: the brand guide (`Brand Guide/brand-guide.html`), the production Tailwind tokens
 * quoted in the Tools redesign handoff, and the live Webflow stylesheet.
 *
 * Collctiv's Webflow project has no spacing scale — padding is hardcoded per class, in the
 * 20/60/40/16/24px range. We define one here. That is a deliberate improvement rather than a
 * mismatch, because our `lp-` classes are self-contained and never inherit from theirs.
 */

export const colour = {
  /** CTA and action only. The brand rule is that pink appears once per view. */
  pink: '#F2167D',
  pinkHover: '#FF2389',

  /** Primary brand colour: headings, chrome, dark sections. */
  indigo: '#1B0273',
  indigoDeep: '#140749',

  /**
   * Decorative only — #0D9BAF is about 3:1 on white, which fails WCAG AA for small text.
   * The live site's "New" pill has this bug today.
   */
  teal: '#0D9BAF',
  /** ~5.7:1 on white. Use wherever teal carries text. */
  tealInk: '#07707E',

  /** Reserved for seasonal / featured treatments. Not the CTA yellow. */
  yellow: '#F3DF43',

  /**
   * The live conversion button colour, which is distinct from brand yellow. Paired with
   * #1E1B4B for its text. Reproduced exactly so generated CTAs match the rest of the site.
   */
  ctaYellow: '#FFDE00',
  ctaYellowInk: '#1E1B4B',

  surface: '#F8F7FA',
  surfaceAlt: '#EBE7FB',
  white: '#FFFFFF',

  /**
   * The de-facto text colour on every live landing page.
   *
   * The stylesheet defines globals `#33404c` and `#1f2933`, but every heading and body block
   * on all seven pages overrides both with `#323f4b`. Matching what ships, not what's declared.
   */
  ink: '#323F4B',
  inkMuted: '#52606D',
  grey: '#798099',
  greyLight: '#E6E8F0',
  border: '#E6E8F0',

  /** 80% purple, used behind the frosted step panel on every /lp/ page. */
  indigoGlass: '#1B0273CC',
} as const;

/** 4px base scale. Keys are step numbers so `space[5]` reads as a scale position. */
export const space = {
  0: '0px',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '24px',
  6: '32px',
  7: '48px',
  8: '64px',
  9: '96px',
  10: '128px',
} as const;

export type SpaceStep = keyof typeof space;

/**
 * GT Walsheim Pro is licensed, so this app never serves it. Figtree is the approved stand-in
 * and is what the editor actually renders. Naming Walsheim first means published pages pick
 * it up from Collctiv's own Webflow font upload.
 */
export const fontStack =
  "'Gt walsheim pro', Figtree, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/**
 * The type scale, measured off the live pages rather than invented.
 *
 * Two things worth knowing:
 *
 * - `display` (64px/91px) is what the site uses for centred section headings. It is very
 *   airy, and on the live site it has **no override until 479px**, so a 64px heading renders
 *   inside a ~748px column at tablet width. We add a 991px tier; that is a deliberate
 *   correction, not a mismatch.
 * - `h1` and `h2` are both 52px but with different line-heights (70 vs 66) — that difference
 *   is real on the site, not a rounding artefact.
 */
export const type = {
  /** Centred section headings. */
  display: { 'font-size': '64px', 'line-height': '91px', 'font-weight': '700', 'letter-spacing': '-2px' },
  h1: { 'font-size': '52px', 'line-height': '70px', 'font-weight': '700', 'letter-spacing': '-2px' },
  /** Feature-row headings. */
  h2: { 'font-size': '52px', 'line-height': '66px', 'font-weight': '700', 'letter-spacing': '0px' },
  /** Panel headings inside the purple band. */
  h2Panel: { 'font-size': '45px', 'line-height': '50px', 'font-weight': '700', 'letter-spacing': '-2px' },
  h3: { 'font-size': '24px', 'line-height': '28px', 'font-weight': '700', 'letter-spacing': '0px' },
  h4: { 'font-size': '20px', 'line-height': '28px', 'font-weight': '700', 'letter-spacing': '0px' },
  /** Hero subhead. */
  lead: { 'font-size': '24px', 'line-height': '34px', 'font-weight': '400', 'letter-spacing': '0px' },
  body: { 'font-size': '20px', 'line-height': '28px', 'font-weight': '400', 'letter-spacing': '0px' },
  /** FAQ answers and other dense copy. */
  bodySm: { 'font-size': '16px', 'line-height': '24px', 'font-weight': '400', 'letter-spacing': '0px' },
  small: { 'font-size': '14px', 'line-height': '20px', 'font-weight': '400', 'letter-spacing': '0px' },
  eyebrow: { 'font-size': '17px', 'line-height': '24px', 'font-weight': '700', 'letter-spacing': '0.5px' },
  /** Stat numbers in the purple band. */
  stat: { 'font-size': '64px', 'line-height': '91px', 'font-weight': '700', 'letter-spacing': '0px' },
  statLabel: { 'font-size': '36px', 'line-height': '44px', 'font-weight': '500', 'letter-spacing': '0px' },
} as const;

export type TypeScale = keyof typeof type;

export const radiusScale = {
  sm: '6px',
  md: '12px',
  lg: '20px',
  pill: '9999px',
} as const;

export const shadow = {
  card: '0 1px 2px rgba(28, 9, 98, 0.06), 0 2px 8px rgba(28, 9, 98, 0.06)',
  lifted: '0 12px 40px rgba(28, 9, 98, 0.12)',
} as const;

/**
 * Page width.
 *
 * 1080px is what every container on every live landing page uses — they all override
 * Webflow's `.w-container` 940px default and never set a responsive override. We match it
 * so a generated section lines up with the sections above and below it.
 */
export const layout = {
  containerMax: '1080px',
  containerNarrow: '760px',
  containerWide: '1400px',
  gutter: '20px',
  gutterMobile: '10px',
} as const;

/**
 * Section background tones. The brand rule from the Tools redesign handoff is that the page
 * surface stays neutral and colour is an accent, never a field — so this is a closed set of
 * four, not a colour picker.
 */
export const tones = {
  white: { bg: colour.white, ink: colour.ink, heading: colour.indigo },
  surface: { bg: colour.surface, ink: colour.ink, heading: colour.indigo },
  indigo: { bg: colour.indigo, ink: '#E3DEFA', heading: colour.white },
  pink: { bg: colour.pink, ink: '#FFE6F1', heading: colour.white },
} as const;

export type ToneName = keyof typeof tones;
